<template>
    <section class="min-h-screen flex items-stretch text-white bg-[#F7F7F7]">
      <!-- Side Menu -->
      <div class="hidden md:flex flex-col justify-between bg-[#090446] text-white w-1/6 p-4">
        <!-- Top Menu -->
        <div class="sticky top-0 bg-[#090446] p-4 z-20">
          <div>
            <p class="text-lg font-semibold">Change and Ambiguity Assessment</p>
            <p class="text-sm mt-2">Start your assessment journey</p>
          </div>
        </div>
  
        <!-- Bottom Menu -->
        <div class="mt-auto">
          <router-link to="/privacy-policy" class="text-white hover:text-[#ab064e]">
            Privacy Policy
          </router-link>
        </div>
      </div>
  
      <!-- Main Content -->
      <div class="flex-1 flex items-center justify-center py-6 md:px-16 px-0 z-0">
        <div id="entry-card" class="w-full sm:max-w-md lg:max-w-lg mx-auto px-6 py-8 z-20 bg-white rounded-[36px] shadow-lg">
          <div class="flex items-center justify-center mb-6">
            <a href="#" class="flex items-center">
              <img src="../assets/images/logo.png" class="h-24" alt="Logo" />
            </a>
          </div>
  
          <!-- Entry Form Section -->
          <div v-if="!submitted">
            <div class="text-center mb-6">
              <h1 class="text-2xl font-bold text-black mb-4">Change and Ambiguity Assessment</h1>
              <p class="text-lg text-black mb-4">
                Welcome! This assessment will help you better understand yourself and how you approach and adapt to change.
              </p>
            </div>

            <form @submit.prevent="handleSubmit">
              <div class="form-group mb-4">
                <label for="name" class="block text-sm font-medium text-gray-700 mb-2">Full Name *</label>
                <input
                  type="text"
                  id="name"
                  v-model="form.name"
                  class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#ab064e] focus:border-transparent text-black"
                  placeholder="Enter your full name"
                  required
                />
                <div v-if="errors.name" class="err text-sm mt-1">{{ errors.name }}</div>
              </div>

              <div class="form-group mb-6">
                <label for="email" class="block text-sm font-medium text-gray-700 mb-2">Email Address *</label>
                <input
                  type="email"
                  id="email"
                  v-model="form.email"
                  class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#ab064e] focus:border-transparent text-black"
                  placeholder="Enter your email address"
                  required
                />
                <div v-if="errors.email" class="err text-sm mt-1">{{ errors.email }}</div>
              </div>

              <div class="mb-4">
                <p class="text-sm text-gray-600">
                  The assessment takes approximately <strong>7-10 minutes</strong> to complete. 
                  Please ensure you have a quiet space to focus on the questions.
                </p>
              </div>
  
              <!-- Submit Button -->
              <button
                type="submit"
                :disabled="isLoading"
                class="relative flex items-center justify-center w-full py-2 text-lg text-white bg-[#0A0446] hover:bg-primary-700 focus:ring-4 focus:ring-primary-200 font-medium rounded-lg text-sm px-5 h-10 py-2.5 text-center disabled:opacity-50"
              >
                <svg v-if="isLoading" class="animate-spin h-5 w-5 text-white absolute" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"></path>
                </svg>
                <span v-else>Start Assessment</span>
              </button>
            </form>
          </div>

          <!-- Loading/Processing State -->
          <div v-else-if="submitted && !assessmentToken" class="text-center">
            <div class="mb-4">
              <svg class="animate-spin h-12 w-12 text-[#ab064e] mx-auto" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"></path>
              </svg>
            </div>
            <p class="text-lg text-black">Setting up your assessment...</p>
          </div>
        </div>
      </div>
    </section>
</template>

<script>
import Swal from 'sweetalert2'
import Api from '../router/api'

export default {
  name: 'GlobalAssessmentEntry',
  data() {
    return {
      form: {
        name: '',
        email: ''
      },
      errors: {},
      isLoading: false,
      submitted: false,
      assessmentToken: null
    }
  },
  methods: {
    validateForm() {
      this.errors = {}
      
      if (!this.form.name.trim()) {
        this.errors.name = 'Name is required'
      }
      
      if (!this.form.email.trim()) {
        this.errors.email = 'Email is required'
      } else if (!this.isValidEmail(this.form.email)) {
        this.errors.email = 'Please enter a valid email address'
      }
      
      return Object.keys(this.errors).length === 0
    },
    
    isValidEmail(email) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      return emailRegex.test(email)
    },
    
    async handleSubmit() {
      if (!this.validateForm()) {
        return
      }
      
      this.isLoading = true
      
      try {
        // Call the API to create a global assessment session
        const response = await Api.createGlobalAssessment(this.form)
        
        if (response.data.success) {
          this.submitted = true
          this.assessmentToken = response.data.token
          
          // Redirect to assessment welcome page with the new token
          setTimeout(() => {
            this.$router.push(`/assessment-welcome/${this.assessmentToken}`)
          }, 1500)
        } else {
          throw new Error(response.data.message || 'Failed to create assessment session')
        }
        
      } catch (error) {
        console.error('Error creating assessment session:', error)
        
        let errorMessage = 'Something went wrong. Please try again.'
        
        if (error.response) {
          if (error.response.status === 422 && error.response.data.errors) {
            // Handle validation errors from backend
            this.errors = error.response.data.errors
            this.isLoading = false
            return
          } else if (error.response.data.message) {
            errorMessage = error.response.data.message
          }
        }
        
        Swal.fire({
          title: 'Error!',
          text: errorMessage,
          icon: 'error',
          confirmButtonText: 'OK'
        })
        
        this.isLoading = false
      }
    }
  }
}
</script>

<style scoped>
.form-group {
  margin-bottom: 1rem;
}

.err {
  color: red;
}

input:focus {
  box-shadow: 0 0 0 3px rgba(171, 6, 78, 0.1);
}
</style>


// WEBPACK FOOTER //
// src/components/GlobalAssessmentEntry.vue